import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Award } from 'lucide-react';

import usePositionApi from './config';
import PositionCompetenciesDrawer from './PositionCompetenciesDrawer';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function PositionsPage() {
  const { t } = useTranslation();
  const { canWrite, hasModule } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  // Faz E (spec 2026-08-08 §4, E4) — pozisyon↔yetkinlik çerçevesi İK yüzeyi.
  // Pozisyonlar sayfası EM modülünde ama bu bölüm PM.Write İK yüzeyi — brief:
  // "pozisyon sayfası hangi modülde olursa olsun bölümün görünürlüğü
  // canWrite('PM') + PM modül var (AuthContext)" — EM yazma izninden BAĞIMSIZ.
  const [competenciesDrawer, setCompetenciesDrawer] = useState({ open: false, row: null });
  const canManageCompetencies = canWrite('PM') && hasModule('PM');

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = usePositionApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.code });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('position.title').toLowerCase()}` });
  }, [t]);

  const handleOpenCompetencies = useCallback((row) => {
    setCompetenciesDrawer({ open: true, row });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    const options = [];
    if (canWrite('EM')) {
      options.push(
        { type: 'edit', label: t('position.edit'), onClick: handleEdit },
        { type: 'divider' },
        { type: 'delete', label: t('position.delete'), onClick: handleDelete },
      );
    }
    if (canManageCompetencies) {
      if (options.length > 0) options.push({ type: 'divider' });
      options.push({
        type: 'competencies',
        icon: Award,
        color: '#7C3AED',
        label: t('positionCompetencies.menuLabel'),
        onClick: handleOpenCompetencies,
      });
    }
    return options;
  }, [t, canWrite, canManageCompetencies, handleEdit, handleDelete, handleOpenCompetencies]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('position.edit') : t('position.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('position.deleteTitle')}
        message={t('position.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      <PositionCompetenciesDrawer
        open={competenciesDrawer.open}
        onClose={() => setCompetenciesDrawer({ open: false, row: null })}
        position={competenciesDrawer.row}
      />

      {useMemo(() => (
        <DataTable
          title={t('position.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('EM')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('EM') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
